import React from 'react';
import { Check, ChevronDown } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { cn } from '@/lib/utils';

interface ReportSectionCardProps {
  title: string;
  description: string;
  open: boolean;
  onOpenChange: (_open: boolean) => void;
  completed?: boolean;
  locked?: boolean;
  children?: React.ReactNode;
}

export const ReportSectionCard: React.FC<ReportSectionCardProps> = ({
  title,
  description,
  open,
  onOpenChange,
  completed = false,
  locked = false,
  children,
}) => {
  const handleToggle = () => {
    if (locked) return;
    onOpenChange(!open);
  };

  return (
    <Card
      className={cn(
        'overflow-hidden transition-opacity',
        locked && 'opacity-70'
      )}
    >
      <button
        type="button"
        onClick={handleToggle}
        disabled={locked}
        aria-expanded={open}
        aria-disabled={locked}
        className={cn(
          'flex w-full items-start justify-between gap-4 p-6 text-left',
          locked
            ? 'cursor-not-allowed'
            : 'cursor-pointer hover:bg-gray-50/80'
        )}
      >
        <div className="flex min-w-0 items-start gap-3">
          {completed && (
            <span className="mt-0.5 flex shrink-0 items-center justify-center rounded-full bg-[#F2FF00] p-[6px]">
              <Check className="h-4 w-4 text-black" strokeWidth={3} />
            </span>
          )}
          <div className="min-w-0">
            <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
            <p className="mt-1 text-sm text-gray-500">{description}</p>
          </div>
        </div>
        <ChevronDown
          className={cn(
            'mt-1 h-5 w-5 shrink-0 text-gray-400 transition-transform duration-300',
            open && !locked && 'rotate-180'
          )}
        />
      </button>
      {open && !locked && (
        <CardContent className="border-t border-gray-100 px-6 pb-6 pt-6">
          {children}
        </CardContent>
      )}
    </Card>
  );
};
